/** Original, procedurally composed ambience: no downloads, samples or autoplay. */
export type SoundTheme = 'light' | 'dark';
export class StillnessAudio {
  private context: AudioContext;
  private master: GainNode;
  private scene: GainNode | null = null;
  private voices: OscillatorNode[] = [];
  private sequence: ReturnType<typeof setInterval> | undefined;
  private stopped = false;
  private muted = false;
  constructor() {
    const Audio = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Audio) throw new Error('Audio is not supported');
    this.context = new Audio();
    this.master = this.context.createGain();
    this.master.gain.value = 0;
    const limiter = this.context.createDynamicsCompressor();
    limiter.threshold.value = -18; limiter.ratio.value = 6;
    this.master.connect(limiter); limiter.connect(this.context.destination);
  }
  async start(theme: SoundTheme, muted: boolean) {
    // Called directly from the play tap to unlock audio on iOS/Android.
    await this.context.resume();
    if (this.stopped) return;
    this.setTheme(theme); this.setMuted(muted);
  }
  setMuted(muted: boolean) {
    this.muted = muted;
    if (this.stopped) return;
    this.master.gain.setTargetAtTime(muted ? 0 : .24, this.context.currentTime, .15);
  }
  setTheme(theme: SoundTheme) {
    if (this.stopped) return;
    const ctx = this.context;
    const now = ctx.currentTime;
    if (this.sequence) clearInterval(this.sequence);
    const oldScene = this.scene;
    oldScene?.gain.setTargetAtTime(0, now, .25);
    for (const voice of this.voices) { try { voice.stop(now + 1.5); } catch { /* already stopped */ } }
    this.voices = [];
    if (oldScene) window.setTimeout(() => oldScene.disconnect(), 1600);
    const scene = ctx.createGain(); scene.gain.value = 0; scene.connect(this.master);
    scene.gain.setTargetAtTime(1, now, .65); this.scene = scene;
    // Day: open C-major/add9 voicing and high pentatonic bells. Night: low D-sus2/5 and sparse soft bells.
    const pad = theme === 'light' ? [130.81,196,261.63,329.63] : [73.42,110,146.83,164.81];
    pad.forEach((frequency, i) => {
      const oscillator = ctx.createOscillator(); const gain = ctx.createGain();
      oscillator.type = 'sine'; oscillator.frequency.value = frequency;
      oscillator.detune.value = i % 2 ? 3 : -3; gain.gain.value = theme === 'light' ? .055 : .07;
      oscillator.connect(gain); gain.connect(scene); oscillator.start(); this.voices.push(oscillator);
      const drift = ctx.createOscillator();const depth = ctx.createGain();drift.frequency.value = .07 + i * .015;depth.gain.value = .012;drift.connect(depth);depth.connect(gain.gain);drift.start();this.voices.push(drift);
    });
    const notes = theme === 'light' ? [523.25,659.25,783.99,587.33,659.25,1046.5,783.99,659.25] : [220,293.66,329.63,440,293.66,220];
    let note = 0;
    const bell = () => {
      if (this.stopped) return;
      const at = ctx.currentTime;
      [1,2].forEach((harmonic, index) => {
        const oscillator = ctx.createOscillator();const envelope = ctx.createGain();
        oscillator.frequency.value = notes[note % notes.length] * harmonic; oscillator.type = 'sine';
        envelope.gain.setValueAtTime(0, at);envelope.gain.linearRampToValueAtTime(index ? .015 : .10, at + .12);
        envelope.gain.exponentialRampToValueAtTime(.0001, at + (theme === 'light' ? 3.5 : 5));
        oscillator.connect(envelope);envelope.connect(scene);oscillator.start(at);oscillator.stop(at + 5.1);
        oscillator.onended = () => { oscillator.disconnect();envelope.disconnect(); };
      });
      note++;
    };
    bell();this.sequence = setInterval(bell, theme === 'light' ? 2600 : 4400);
    this.setMuted(this.muted);
  }
  stop() {
    if (this.stopped) return;
    this.stopped = true;
    if (this.sequence) clearInterval(this.sequence);
    this.master.gain.setTargetAtTime(0, this.context.currentTime, .08);
    window.setTimeout(() => { void this.context.close().catch(() => {}); }, 400);
  }
}
